import { computed, ref } from 'vue';
import translationPreferencesAPI from 'dashboard/api/translationPreferences';

const defaultPreferences = () => ({
  translation_enabled: true,
  auto_translate_enabled: false,
  auto_translate_target_language: 'zh',
  auto_translate_activated_at: null,
});

const preferencesState = ref(defaultPreferences());
const loaded = ref(false);
const loadedAccountId = ref(null);
let loadingPromise = null;

const normalizePreferences = raw => ({
  translation_enabled: raw?.translation_enabled ?? true,
  auto_translate_enabled: raw?.auto_translate_enabled ?? false,
  auto_translate_target_language:
    raw?.auto_translate_target_language ?? 'zh',
  auto_translate_activated_at: raw?.auto_translate_activated_at ?? null,
});

const normalizeAccountId = accountId => {
  const numericAccountId = Number(accountId);
  return Number.isFinite(numericAccountId) && numericAccountId > 0
    ? numericAccountId
    : null;
};

export const useTranslationPreferences = () => {
  const setPreferences = (raw, { accountId = null } = {}) => {
    preferencesState.value = normalizePreferences(raw);
    loaded.value = true;
    loadedAccountId.value = normalizeAccountId(accountId);
    return preferencesState.value;
  };

  const ensureLoaded = async ({ force = false, accountId = null } = {}) => {
    const normalizedAccountId = normalizeAccountId(accountId);
    const hasAccountContext = normalizedAccountId !== null;
    const accountChanged =
      hasAccountContext && loadedAccountId.value !== normalizedAccountId;

    if (loaded.value && !force && !accountChanged) {
      return preferencesState.value;
    }

    if (loadingPromise && !force && !accountChanged) {
      return loadingPromise;
    }

    loadingPromise = translationPreferencesAPI
      .get()
      .then(response =>
        setPreferences(response.data, { accountId: normalizedAccountId })
      )
      .finally(() => {
        loadingPromise = null;
      });

    return loadingPromise;
  };

  return {
    preferences: computed(() => preferencesState.value),
    loaded: computed(() => loaded.value),
    loadedAccountId: computed(() => loadedAccountId.value),
    ensureLoaded,
    setPreferences,
  };
};
